import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import type { JoinRequestsAccountItem } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

export type JoinRequestAccount = JoinRequestsAccountItem;

export type JoinRequestAccounts = {
  /** The verified signup email the join token was minted for. */
  email: string;
  accounts: JoinRequestAccount[];
};

/**
 * List the orgs a verified signup can request to join.
 *
 * Hits the unauthenticated token-scoped endpoint; the token comes from the
 * signup verify-code response. A 404 covers every invalid-token shape
 * (missing, expired, consumed — deliberately indistinguishable) and resolves
 * to null so the page can show the link-expired state rather than throwing.
 */
export async function fetchJoinRequestAccounts(token: string): Promise<JoinRequestAccounts | null> {
  try {
    const data = await hydraUiApi.joinRequests.getJoinRequestTokensAccounts({
      params: { path: { token } },
    });
    return { email: data.email, accounts: data.accounts };
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export type CreateJoinRequestInput = {
  accountId: string;
  firstName: string;
  lastName: string;
};

/**
 * Ask an org's admins to let this signup join (consumes the join token).
 * The response is uniform ("sent") whether or not a live request already
 * existed, so callers can't distinguish a pending request from a declined one.
 * A 404 (token expired/consumed) propagates as HydraUIAPIError for the page
 * to map to the link-expired state.
 */
export async function createJoinRequest(token: string, input: CreateJoinRequestInput): Promise<void> {
  await hydraUiApi.joinRequests.joinRequestTokensJoinRequests({
    params: { path: { token } },
    body: {
      account_id: input.accountId,
      first_name: input.firstName,
      last_name: input.lastName,
    },
  });
}

/**
 * Signup URL that resumes at the password step for an already-verified email,
 * skipping the code re-entry. Used by the join screen's "create a new account
 * instead" links: the requester proved email ownership with the code, so
 * Ember rebuilds the flow and lands exactly where confirming the code lands.
 */
export function signupResumeHref(email: string): string {
  return `/signup?email=${encodeURIComponent(email)}&resume=true`;
}
